import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../services/apiConfig';
import './FrameworkBuilder.css';

// Use centralized API config for environment-aware URL resolution
const API_BASE_URL = getApiBaseUrl();

interface Framework {
  id: string;
  name: string;
  code: string;
  type: string;
  dimensions?: FrameworkDimension[];
  components?: FrameworkComponent[];
  attributes?: FrameworkAttribute[];
}

interface FrameworkDimension {
  mapping_id: string;
  id: string;
  name: string;
  description: string;
  weight_override: number | null;
  display_order: number;
  is_mandatory: boolean;
}

interface FrameworkComponent {
  mapping_id: string;
  id: string;
  name: string;
  description: string;
  dimension_id: string;
  weight_override: number | null;
  display_order: number;
  is_mandatory: boolean;
}

interface FrameworkAttribute {
  mapping_id: string;
  id: string;
  name: string;
  description: string;
  component_id: string;
  default_weight: number;
  weight_override: number | null;
  display_order: number;
  is_active: boolean;
}

interface LibraryDimension {
  id: string;
  name: string;
  description: string;
  display_order: number;
}

interface LibraryComponent {
  id: string;
  name: string;
  description: string;
  dimensionId: string;
  displayOrder: number;
}

interface LibraryAttribute {
  id: string;
  name: string;
  description: string;
  componentId: string;
  weight: number;
  displayOrder: number;
}

// Weight color function (same as used in other parts of the app)
const getWeightColor = (weight: number): string => {
  if (weight >= 4.5) return '#4caf50'; // Green - High
  if (weight >= 3.5) return '#8bc34a'; // Light Green
  if (weight >= 2.5) return '#ffc107'; // Yellow/Amber - Medium
  if (weight >= 1.5) return '#ff9800'; // Orange
  return '#f44336'; // Red - Low
};

const FrameworkBuilder: React.FC = () => {
  const [frameworks, setFrameworks] = useState<Framework[]>([]);
  const [selectedFramework, setSelectedFramework] = useState<Framework | null>(null);
  const [frameworkData, setFrameworkData] = useState<Framework | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingLibrary, setLoadingLibrary] = useState(true);

  // Library data
  const [libraryDimensions, setLibraryDimensions] = useState<LibraryDimension[]>([]);
  const [libraryComponents, setLibraryComponents] = useState<LibraryComponent[]>([]);
  const [libraryAttributes, setLibraryAttributes] = useState<LibraryAttribute[]>([]);

  // Selected items for property editing
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [selectedItemType, setSelectedItemType] = useState<'dimension' | 'component' | 'attribute' | null>(null);

  // Expanded state for tree
  const [expandedDimensions, setExpandedDimensions] = useState<Set<string>>(new Set());
  const [expandedComponents, setExpandedComponents] = useState<Set<string>>(new Set());

  // Search/filter
  const [librarySearch, setLibrarySearch] = useState('');
  const [libraryFilter, setLibraryFilter] = useState<'all' | 'dimensions' | 'components' | 'attributes'>('all');

  // Dimension selection for adding components (since components are independent)
  const [pendingComponent, setPendingComponent] = useState<LibraryComponent | null>(null);
  const [pendingAttribute, setPendingAttribute] = useState<LibraryAttribute | null>(null);
  const [showDimensionSelector, setShowDimensionSelector] = useState(false);

  useEffect(() => {
    const initData = async () => {
      setLoading(true);
      setLoadingLibrary(true);
      await Promise.all([
        loadFrameworks(),
        loadLibraryData()
      ]);
      setLoading(false);
      setLoadingLibrary(false);
    };
    initData();
  }, []);

  useEffect(() => {
    if (selectedFramework) {
      loadFrameworkDetails(selectedFramework.id);
    }
  }, [selectedFramework]);

  const loadFrameworks = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE_URL}/frameworks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFrameworks(response.data);
      if (response.data.length > 0 && !selectedFramework) {
        setSelectedFramework(response.data[0]);
      }
    } catch (error) {
      console.error('Error loading frameworks:', error);
    }
  };

  const loadFrameworkDetails = async (frameworkId: string) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE_URL}/frameworks/${frameworkId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFrameworkData(response.data);

      // Auto-expand all dimensions
      const dimIds = new Set<string>(response.data.dimensions.map((d: any) => d.id));
      setExpandedDimensions(dimIds);
    } catch (error) {
      console.error('Error loading framework details:', error);
      alert('Failed to load framework details');
    }
  };

  const loadLibraryData = async () => {
    try {
      const token = localStorage.getItem('authToken');

      const [dims, comps, attrs] = await Promise.all([
        axios.get(`${API_BASE_URL}/dimensions`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_BASE_URL}/components`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_BASE_URL}/attributes`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      setLibraryDimensions(dims.data);
      setLibraryComponents(comps.data);
      setLibraryAttributes(attrs.data);
    } catch (error) {
      console.error('Error loading library data:', error);
    }
  };

  const toggleDimension = (dimensionId: string) => {
    const newExpanded = new Set(expandedDimensions);
    if (newExpanded.has(dimensionId)) {
      newExpanded.delete(dimensionId);
    } else {
      newExpanded.add(dimensionId);
    }
    setExpandedDimensions(newExpanded);
  };

  const toggleComponent = (componentId: string) => {
    const newExpanded = new Set(expandedComponents);
    if (newExpanded.has(componentId)) {
      newExpanded.delete(componentId);
    } else {
      newExpanded.add(componentId);
    }
    setExpandedComponents(newExpanded);
  };

  const handleSelectDimension = (dim: FrameworkDimension) => {
    setSelectedItem(dim);
    setSelectedItemType('dimension');
  };

  const handleSelectComponent = (comp: FrameworkComponent) => {
    setSelectedItem(comp);
    setSelectedItemType('component');
  };

  const handleSelectAttribute = (attr: FrameworkAttribute) => {
    setSelectedItem(attr);
    setSelectedItemType('attribute');
  };

  const handleAddDimension = async (dimension: LibraryDimension) => {
    if (!selectedFramework) return;

    // Check if already exists
    if (frameworkData?.dimensions?.some(d => d.id === dimension.id)) {
      alert('This dimension is already in the framework');
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      await axios.post(
        `${API_BASE_URL}/frameworks/${selectedFramework.id}/dimensions`,
        {
          dimension_id: dimension.id,
          display_order: frameworkData?.dimensions?.length || 0,
          weight_override: null,
          is_mandatory: true
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      await loadFrameworkDetails(selectedFramework.id);
      alert('Dimension added successfully');
    } catch (error: any) {
      console.error('Error adding dimension:', error);
      alert(error.response?.data?.error || 'Failed to add dimension');
    }
  };

  const handleAddComponent = async (component: LibraryComponent) => {
    if (!selectedFramework) return;

    // Check if already exists
    if (frameworkData?.components?.some(c => c.id === component.id)) {
      alert('This component is already in the framework');
      return;
    }

    // Check if framework has dimensions
    if (!frameworkData?.dimensions || frameworkData.dimensions.length === 0) {
      alert('Please add at least one dimension to the framework first');
      return;
    }

    // If only one dimension, assign automatically; otherwise show selector
    if (frameworkData.dimensions.length === 1) {
      await addComponentToDimension(component, frameworkData.dimensions[0].id);
    } else {
      setPendingComponent(component);
      setShowDimensionSelector(true);
    }
  };

  const addComponentToDimension = async (component: LibraryComponent, dimensionId: string) => {
    if (!selectedFramework) return;

    try {
      const token = localStorage.getItem('authToken');
      const componentsInDim = frameworkData?.components?.filter(c => c.dimension_id === dimensionId) || [];

      // Add the component first
      await axios.post(
        `${API_BASE_URL}/frameworks/${selectedFramework.id}/components`,
        {
          dimension_id: dimensionId,
          component_id: component.id,
          display_order: componentsInDim.length,
          weight_override: null,
          is_mandatory: true
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Find all attributes linked to this component and add them
      const linkedAttributes = libraryAttributes.filter(a => a.componentId === component.id);
      const existingAttrIds = new Set(frameworkData?.attributes?.map(a => a.id) || []);

      let addedCount = 0;
      for (let i = 0; i < linkedAttributes.length; i++) {
        const attr = linkedAttributes[i];
        if (!existingAttrIds.has(attr.id)) {
          try {
            await axios.post(
              `${API_BASE_URL}/frameworks/${selectedFramework.id}/attributes`,
              {
                component_id: component.id,
                attribute_id: attr.id,
                display_order: i,
                weight_override: null,
                is_active: true
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            addedCount++;
          } catch (err) {
            console.error(`Error adding attribute ${attr.name}:`, err);
          }
        }
      }

      await loadFrameworkDetails(selectedFramework.id);
      alert(`Component added successfully with ${addedCount} attribute(s)`);
    } catch (error: any) {
      console.error('Error adding component:', error);
      alert(error.response?.data?.error || 'Failed to add component');
    }
  };

  const handleDimensionSelect = async (dimensionId: string) => {
    if (pendingComponent) {
      await addComponentToDimension(pendingComponent, dimensionId);
      setPendingComponent(null);
      setPendingAttribute(null);
      setShowDimensionSelector(false);
    } else if (pendingAttribute) {
      // Find the parent component and add it with all attributes
      const parentComponent = libraryComponents.find(c => c.id === pendingAttribute.componentId);
      if (parentComponent) {
        await addComponentToDimension(parentComponent, dimensionId);
      }
      setPendingComponent(null);
      setPendingAttribute(null);
      setShowDimensionSelector(false);
    }
  };

  const cancelDimensionSelect = () => {
    setPendingComponent(null);
    setPendingAttribute(null);
    setShowDimensionSelector(false);
  };

  const handleAddAttribute = async (attribute: LibraryAttribute) => {
    if (!selectedFramework) return;

    // Check if framework has dimensions
    if (!frameworkData?.dimensions || frameworkData.dimensions.length === 0) {
      alert('Please add at least one dimension to the framework first');
      return;
    }

    // Check if parent component exists in framework
    const parentComponentInFramework = frameworkData?.components?.find(c => c.id === attribute.componentId);

    if (parentComponentInFramework) {
      // Component already in framework - add all missing attributes for this component
      await addAllAttributesForComponent(attribute.componentId);
    } else {
      // Component not in framework - need to add component first (with dimension selection)
      const parentComponent = libraryComponents.find(c => c.id === attribute.componentId);
      if (!parentComponent) {
        alert('Parent component not found in library');
        return;
      }

      // If only one dimension, add automatically; otherwise show selector
      if (frameworkData.dimensions.length === 1) {
        await addComponentToDimension(parentComponent, frameworkData.dimensions[0].id);
      } else {
        setPendingAttribute(attribute);
        setShowDimensionSelector(true);
      }
    }
  };

  const addAllAttributesForComponent = async (componentId: string) => {
    if (!selectedFramework) return;

    try {
      const token = localStorage.getItem('authToken');

      // Find all attributes linked to this component
      const linkedAttributes = libraryAttributes.filter(a => a.componentId === componentId);
      const existingAttrIds = new Set(frameworkData?.attributes?.map(a => a.id) || []);
      const existingAttrsInComp = frameworkData?.attributes?.filter(a => a.component_id === componentId) || [];

      let addedCount = 0;
      for (let i = 0; i < linkedAttributes.length; i++) {
        const attr = linkedAttributes[i];
        if (!existingAttrIds.has(attr.id)) {
          try {
            await axios.post(
              `${API_BASE_URL}/frameworks/${selectedFramework.id}/attributes`,
              {
                component_id: componentId,
                attribute_id: attr.id,
                display_order: existingAttrsInComp.length + addedCount,
                weight_override: null,
                is_active: true
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            addedCount++;
          } catch (err) {
            console.error(`Error adding attribute ${attr.name}:`, err);
          }
        }
      }

      await loadFrameworkDetails(selectedFramework.id);
      if (addedCount > 0) {
        alert(`${addedCount} attribute(s) added successfully`);
      } else {
        alert('All attributes for this component are already in the framework');
      }
    } catch (error: any) {
      console.error('Error adding attributes:', error);
      alert(error.response?.data?.error || 'Failed to add attributes');
    }
  };

  const handleRemoveItem = async () => {
    if (!selectedFramework || !selectedItem || !selectedItemType) return;

    if (!window.confirm(`Remove "${selectedItem.name}" from framework?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const endpoint = `${API_BASE_URL}/frameworks/${selectedFramework.id}/${selectedItemType}s/${selectedItem.mapping_id}`;

      await axios.delete(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });

      await loadFrameworkDetails(selectedFramework.id);
      setSelectedItem(null);
      setSelectedItemType(null);
      alert('Item removed successfully');
    } catch (error: any) {
      console.error('Error removing item:', error);
      alert(error.response?.data?.error || 'Failed to remove item');
    }
  };

  const handleUpdateProperty = async (property: string, value: any) => {
    if (!selectedFramework || !selectedItem || !selectedItemType) return;

    try {
      const token = localStorage.getItem('authToken');
      const endpoint = `${API_BASE_URL}/frameworks/${selectedFramework.id}/${selectedItemType}s/${selectedItem.mapping_id}`;

      const updateData: any = {
        display_order: selectedItem.display_order,
        weight_override: selectedItem.weight_override,
        is_mandatory: selectedItem.is_mandatory !== undefined ? selectedItem.is_mandatory : true,
        is_active: selectedItem.is_active !== undefined ? selectedItem.is_active : true
      };

      if (selectedItemType === 'component') {
        updateData.dimension_id = selectedItem.dimension_id;
      } else if (selectedItemType === 'attribute') {
        updateData.component_id = selectedItem.component_id;
      }

      updateData[property] = value;

      await axios.put(endpoint, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSelectedItem({ ...selectedItem, [property]: value });
      await loadFrameworkDetails(selectedFramework.id);
    } catch (error: any) {
      console.error('Error updating property:', error);
      alert(error.response?.data?.error || 'Failed to update property');
    }
  };

  const filteredLibraryItems = () => {
    const search = librarySearch.toLowerCase();
    const items: Array<{ type: string; item: any }> = [];

    if (libraryFilter === 'all' || libraryFilter === 'dimensions') {
      libraryDimensions
        .filter(d => d.name.toLowerCase().includes(search) || d.description?.toLowerCase().includes(search))
        .forEach(d => items.push({ type: 'dimension', item: d }));
    }

    if (libraryFilter === 'all' || libraryFilter === 'components') {
      libraryComponents
        .filter(c => c.name.toLowerCase().includes(search) || c.description?.toLowerCase().includes(search))
        .forEach(c => items.push({ type: 'component', item: c }));
    }

    if (libraryFilter === 'all' || libraryFilter === 'attributes') {
      libraryAttributes
        .filter(a => a.name.toLowerCase().includes(search) || a.description?.toLowerCase().includes(search))
        .forEach(a => items.push({ type: 'attribute', item: a }));
    }

    return items;
  };

  const getComponentsForDimension = (dimensionId: string) => {
    return frameworkData?.components?.filter(c => c.dimension_id === dimensionId).sort((a, b) => a.display_order - b.display_order) || [];
  };

  const getAttributesForComponent = (componentId: string) => {
    return frameworkData?.attributes?.filter(a => a.component_id === componentId).sort((a, b) => a.display_order - b.display_order) || [];
  };

  // Helper functions to check if items are already in framework
  const isAlreadyInFramework = (type: string, item: any): boolean => {
    if (type === 'dimension') {
      return frameworkData?.dimensions?.some(d => d.id === item.id) || false;
    } else if (type === 'component') {
      return frameworkData?.components?.some(c => c.id === item.id) || false;
    } else if (type === 'attribute') {
      return frameworkData?.attributes?.some(a => a.id === item.id) || false;
    }
    return false;
  };

  return (
    <div className="framework-builder-container">
      <div className="framework-builder-header">
        <div>
          <h1>Framework Builder</h1>
          <p>Build and customize framework structure</p>
        </div>
        <div className="framework-selector">
          <label>Select Framework to Build or Edit:</label>
          <select
            value={selectedFramework?.id || ''}
            onChange={(e) => {
              const fw = frameworks.find(f => f.id === e.target.value);
              setSelectedFramework(fw || null);
            }}
          >
            <option value="" disabled>-- Choose a framework --</option>
            {frameworks.map(fw => (
              <option key={fw.id} value={fw.id}>{fw.name} ({fw.code})</option>
            ))}
          </select>
        </div>
      </div>

      <div className="framework-builder-content">
        {/* Left Pane - Library */}
        <div className="library-pane">
          <div className="library-header">
            <h2>Library</h2>
            <div className="library-filters">
              <input
                type="text"
                placeholder="Search..."
                value={librarySearch}
                onChange={(e) => setLibrarySearch(e.target.value)}
                className="library-search"
              />
              <select value={libraryFilter} onChange={(e) => setLibraryFilter(e.target.value as any)}>
                <option value="all">All</option>
                <option value="dimensions">Dimensions</option>
                <option value="components">Components</option>
                <option value="attributes">Attributes</option>
              </select>
            </div>
          </div>

          <div className="library-items">
            {loadingLibrary ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                Loading library data...
              </div>
            ) : filteredLibraryItems().length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                No items found. {libraryDimensions.length === 0 ? 'Please check your authentication.' : 'Try adjusting your search/filter.'}
              </div>
            ) : (
              filteredLibraryItems().map((entry, idx) => {
                const alreadyInFramework = isAlreadyInFramework(entry.type, entry.item);
                return (
                  <div
                    key={idx}
                    className={`library-item library-item-${entry.type} ${alreadyInFramework ? 'library-item-disabled' : ''}`}
                    style={{
                      opacity: alreadyInFramework ? 0.5 : 1,
                      backgroundColor: alreadyInFramework ? '#f5f5f5' : 'white'
                    }}
                  >
                    <div className="library-item-header">
                      <span className="library-item-type">{entry.type}</span>
                      <h4>{entry.item.name}</h4>
                    </div>
                    {entry.item.description && (
                      <p className="library-item-description">{entry.item.description}</p>
                    )}
                    {alreadyInFramework && (
                      <div style={{
                        fontSize: '12px',
                        color: '#666',
                        fontStyle: 'italic',
                        marginBottom: '8px'
                      }}>
                        ✓ Already in framework
                      </div>
                    )}
                    <button
                      className="btn-add-to-framework"
                      onClick={() => {
                        if (entry.type === 'dimension') handleAddDimension(entry.item);
                        else if (entry.type === 'component') handleAddComponent(entry.item);
                        else if (entry.type === 'attribute') handleAddAttribute(entry.item);
                      }}
                      disabled={alreadyInFramework}
                      style={{
                        cursor: alreadyInFramework ? 'not-allowed' : 'pointer',
                        opacity: alreadyInFramework ? 0.5 : 1
                      }}
                    >
                      {alreadyInFramework ? '✓ In Framework' : '+ Add to Framework'}
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Middle Pane - Framework Structure Tree */}
        <div className="structure-pane">
          <div className="structure-header">
            <h2>Framework Structure</h2>
            <div className="structure-stats">
              {frameworkData && (
                <>
                  <span>{frameworkData.dimensions?.length || 0}D</span>
                  <span>{frameworkData.components?.length || 0}C</span>
                  <span>{frameworkData.attributes?.length || 0}A</span>
                </>
              )}
            </div>
          </div>

          <div className="structure-tree">
            {frameworkData?.dimensions?.sort((a, b) => a.display_order - b.display_order).map((dimension) => (
              <div key={dimension.id} className="tree-dimension">
                <div
                  className={`tree-node tree-dimension-node ${selectedItem?.id === dimension.id ? 'selected' : ''}`}
                  onClick={() => handleSelectDimension(dimension)}
                >
                  <button
                    className="tree-expand-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleDimension(dimension.id);
                    }}
                  >
                    {expandedDimensions.has(dimension.id) ? '▼' : '▶'}
                  </button>
                  <span className="tree-node-icon">📊</span>
                  <span className="tree-node-label">{dimension.name}</span>
                  {dimension.weight_override && (
                    <span className="tree-node-badge">W:{dimension.weight_override}</span>
                  )}
                </div>

                {expandedDimensions.has(dimension.id) && (
                  <div className="tree-children">
                    {getComponentsForDimension(dimension.id).map((component) => (
                      <div key={component.id} className="tree-component">
                        <div
                          className={`tree-node tree-component-node ${selectedItem?.id === component.id ? 'selected' : ''}`}
                          onClick={() => handleSelectComponent(component)}
                        >
                          <button
                            className="tree-expand-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleComponent(component.id);
                            }}
                          >
                            {expandedComponents.has(component.id) ? '▼' : '▶'}
                          </button>
                          <span className="tree-node-icon">📦</span>
                          <span className="tree-node-label">{component.name}</span>
                          {component.weight_override && (
                            <span className="tree-node-badge">W:{component.weight_override}</span>
                          )}
                        </div>

                        {expandedComponents.has(component.id) && (
                          <div className="tree-children">
                            {getAttributesForComponent(component.id).map((attribute) => {
                              const effectiveWeight = attribute.weight_override || attribute.default_weight;
                              const weightColor = getWeightColor(effectiveWeight);

                              return (
                                <div
                                  key={attribute.id}
                                  className={`tree-node tree-attribute-node ${selectedItem?.id === attribute.id ? 'selected' : ''} ${!attribute.is_active ? 'inactive' : ''}`}
                                  onClick={() => handleSelectAttribute(attribute)}
                                  style={{
                                    borderLeftColor: weightColor
                                  }}
                                >
                                  <span className="tree-node-icon">{attribute.is_active ? '✓' : '○'}</span>
                                  <span className="tree-node-label">{attribute.name}</span>
                                  <span
                                    className="tree-node-badge"
                                    style={{
                                      backgroundColor: weightColor,
                                      color: 'white'
                                    }}
                                  >
                                    W:{effectiveWeight}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Right Pane - Properties Editor */}
        <div className="properties-pane">
          <div className="properties-header">
            <h2>Properties</h2>
          </div>

          {selectedItem && selectedItemType ? (
            <div className="properties-content">
              <div className="property-section">
                <h3>{selectedItem.name}</h3>
                <p className="property-description">{selectedItem.description}</p>
              </div>

              <div className="property-section">
                <h4>Configuration</h4>

                <div className="property-field">
                  <label>Display Order</label>
                  <input
                    type="number"
                    value={selectedItem.display_order}
                    onChange={(e) => handleUpdateProperty('display_order', parseInt(e.target.value))}
                    min="0"
                  />
                </div>

                {selectedItemType !== 'dimension' && (
                  <div className="property-field">
                    <label>Weight Override</label>
                    <input
                      type="number"
                      value={selectedItem.weight_override || ''}
                      onChange={(e) => handleUpdateProperty('weight_override', e.target.value ? parseFloat(e.target.value) : null)}
                      placeholder={selectedItemType === 'attribute' ? `Default: ${selectedItem.default_weight}` : 'No override'}
                      min="1"
                      max="5"
                      step="0.1"
                    />
                    <small>Leave empty to use default weight</small>
                  </div>
                )}

                {selectedItemType !== 'attribute' && (
                  <div className="property-field">
                    <label>
                      <input
                        type="checkbox"
                        checked={selectedItem.is_mandatory}
                        onChange={(e) => handleUpdateProperty('is_mandatory', e.target.checked)}
                      />
                      Mandatory
                    </label>
                    <small>Must be included in this framework</small>
                  </div>
                )}

                {selectedItemType === 'attribute' && (
                  <div className="property-field">
                    <label>
                      <input
                        type="checkbox"
                        checked={selectedItem.is_active}
                        onChange={(e) => handleUpdateProperty('is_active', e.target.checked)}
                      />
                      Active
                    </label>
                    <small>Participates in scoring calculations</small>
                  </div>
                )}
              </div>

              <div className="property-actions">
                <button className="btn-remove" onClick={handleRemoveItem}>
                  Remove from Framework
                </button>
              </div>
            </div>
          ) : (
            <div className="properties-empty">
              <p>Select an item from the structure tree to edit its properties</p>
            </div>
          )}
        </div>
      </div>

      {/* Dimension Selection Modal */}
      {showDimensionSelector && (pendingComponent || pendingAttribute) && (
        <div className="modal-overlay" onClick={cancelDimensionSelect}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>Select Target Dimension</h2>
              <button className="modal-close" onClick={cancelDimensionSelect}>&times;</button>
            </div>
            <div className="modal-body">
              {pendingComponent ? (
                <p style={{ marginBottom: '16px', color: '#666' }}>
                  Select which dimension to assign the component "<strong>{pendingComponent.name}</strong>" to:
                </p>
              ) : pendingAttribute ? (
                <p style={{ marginBottom: '16px', color: '#666' }}>
                  The parent component "<strong>{libraryComponents.find(c => c.id === pendingAttribute.componentId)?.name}</strong>"
                  will be added along with all its attributes. Select which dimension to assign it to:
                </p>
              ) : null}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {frameworkData?.dimensions?.map((dim) => (
                  <button
                    key={dim.id}
                    onClick={() => handleDimensionSelect(dim.id)}
                    style={{
                      padding: '12px 16px',
                      textAlign: 'left',
                      background: '#f5f5f5',
                      border: '2px solid #e0e0e0',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.borderColor = '#1976d2';
                      e.currentTarget.style.background = '#e3f2fd';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.borderColor = '#e0e0e0';
                      e.currentTarget.style.background = '#f5f5f5';
                    }}
                  >
                    <div style={{ fontWeight: '600', color: '#333' }}>{dim.name}</div>
                    {dim.description && (
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>{dim.description}</div>
                    )}
                  </button>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={cancelDimensionSelect}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FrameworkBuilder;
